import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Gift, Copy, Users, Check, Share2, Trophy, Crown } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function Referrals() {
  const [info, setInfo] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referral/info").then(r => setInfo(r.data));
    api.get("/referral/leaderboard").then(r => setLeaderboard(r.data.leaderboard || []));
  }, []);

  const inviteUrl = info ? `${window.location.origin}/login?ref=${info.referral_code}` : "";

  const copy = async () => {
    await navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "AfroRichMind", text: "Your AI Financial Coach — personalized to your actual money. Use my code for early access:", url: inviteUrl });
      } catch {}
    } else copy();
  };

  if (!info) return <div className="p-8">Loading...</div>;

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <Gift className="w-6 h-6 text-[color:var(--gold)]" />
        <h1 className="font-heading text-3xl font-bold">Referrals</h1>
      </div>
      <p className="text-[color:var(--text-secondary)] mb-8">Give a friend a smarter money coach. Get 1 free month for every friend who subscribes.</p>

      <div className="glass-card p-8 mb-6 relative overflow-hidden">
        <div className="absolute inset-0 shimmer pointer-events-none" />
        <div className="relative">
          <div className="text-xs uppercase tracking-widest text-[color:var(--gold)] mb-3">Your referral code</div>
          <div data-testid="referral-code" className="font-mono text-4xl lg:text-5xl font-bold gold-gradient-text mb-6">{info.referral_code}</div>

          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="flex-1 px-4 py-3 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] font-mono text-sm truncate">
              {inviteUrl}
            </div>
            <button data-testid="copy-referral-btn" onClick={copy} className="gold-btn px-5 py-3 rounded-xl inline-flex items-center gap-2 justify-center">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy link"}
            </button>
            <button data-testid="share-referral-btn" onClick={share} className="px-5 py-3 rounded-xl border border-[rgba(212,175,55,0.3)] hover:border-[color:var(--gold)] inline-flex items-center gap-2 justify-center">
              <Share2 className="w-4 h-4" /> Share
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-8">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-5">
          <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-2">Invited</div>
          <div data-testid="stat-invited" className="font-mono text-3xl font-bold">{info.total_invited}</div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass-card p-5">
          <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-2">Subscribed</div>
          <div data-testid="stat-subscribed" className="font-mono text-3xl font-bold text-[color:var(--emerald)]">{info.successful_referrals}</div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="glass-card p-5 border-[rgba(212,175,55,0.3)]">
          <div className="text-xs uppercase tracking-widest text-[color:var(--gold)] mb-2">Free months earned</div>
          <div data-testid="stat-credits" className="font-mono text-3xl font-bold gold-gradient-text">{info.credited_months}</div>
        </motion.div>
      </div>

      <div>
        <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><Users className="w-4 h-4" /> Your invites</h2>
        <div className="glass-card divide-y divide-[rgba(212,175,55,0.08)] mb-8">
          {info.referred_list.length === 0 && (
            <div className="p-8 text-center text-sm text-[color:var(--text-muted)]">No invites yet. Share your link above.</div>
          )}
          {info.referred_list.map((r) => (
            <div key={r.user_id} className="p-4 flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">{r.name}</div>
                <div className="text-xs text-[color:var(--text-muted)]">{r.email}</div>
              </div>
              <div className={`text-xs px-3 py-1 rounded-full ${r.tier === "free" ? "bg-[rgba(255,255,255,0.05)] text-[color:var(--text-muted)]" : "bg-[rgba(42,157,143,0.15)] text-[color:var(--emerald)]"}`}>
                {r.tier === "free" ? "Signed up" : `Subscribed · ${r.tier}`}
              </div>
            </div>
          ))}
        </div>

        <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><Trophy className="w-4 h-4 text-[color:var(--gold)]" /> Leaderboard</h2>
        <div data-testid="referral-leaderboard" className="glass-card divide-y divide-[rgba(212,175,55,0.08)]">
          {leaderboard.length === 0 && (
            <div className="p-8 text-center text-sm text-[color:var(--text-muted)]">Be the first on the leaderboard. Share your link.</div>
          )}
          {leaderboard.map((row, i) => (
            <div key={i} data-testid={`leaderboard-row-${i}`} className={`p-4 flex items-center gap-4 ${row.is_you ? "bg-[rgba(212,175,55,0.06)]" : ""}`}>
              <div className="w-10 text-center">
                {row.rank === 1 ? <Crown className="w-5 h-5 text-[color:var(--gold)] mx-auto" />
                  : row.rank ? <span className="font-mono font-bold text-[color:var(--text-secondary)]">#{row.rank}</span>
                    : <span className="text-xs text-[color:var(--text-muted)]">—</span>}
              </div>
              <div className="w-9 h-9 rounded-full bg-[rgba(212,175,55,0.15)] flex items-center justify-center text-[color:var(--gold)] font-semibold text-sm">
                {row.initial}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate flex items-center gap-2">
                  {row.name}
                  {row.is_you && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(212,175,55,0.15)] text-[color:var(--gold)] uppercase tracking-widest">You</span>}
                </div>
                <div className="text-xs text-[color:var(--text-muted)]">{row.invited} invited</div>
              </div>
              <div className="text-right">
                <div className="font-mono font-bold gold-gradient-text">{row.successful}</div>
                <div className="text-[10px] text-[color:var(--text-muted)] uppercase tracking-widest">subs</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
